import { ClientError } from 'graphql-request';
import { z } from 'zod';

import type {
  ListingOfferFinancingType,
  ListingOfferLenderType,
  SubmitListingOfferInput,
} from '../../__generated__/zeus/types';
import { requestZeusFromBrowser } from '../api/zeusBrowserClient';
import { isValidEmailAddress } from '../shared/emailValidation';
import { e164UsPhone } from '../shared/phone';
import {
  SubmitAgentListingOfferDocument,
  type SubmitAgentListingOfferMutation,
} from './__generated__/offer-submission.zeus';
import { documentsForFinancing } from './financingDocument';
import { type ListingDetails, showsAgentLicenseNumber, showsMlsOfficeId } from './listingDetails';
import { AGENT_TYPES, type OfferDraft } from './offerDraft';

/**
 * Submission of the reviewed draft through Zeus's `submitListingOffer`
 * mutation — the strict-intake `ResaleListingOffer.SubmitListingOffer` RPC.
 * Intake validates every term server-side and answers with typed per-field
 * violations instead of creating the offer; transport and auth failures
 * arrive as GraphQL errors with a `code` extension.
 */
const FINANCING_CHOICES = ['cash', 'conventional', 'fha', 'va', 'usda', 'other'] as const;

// Intake writes to int4 cents columns; anything above is certain to reject.
export const MAX_CENTS = 2_147_483_647;

// Intake attaches at most this many documents to one offer.
export const MAX_OFFER_DOCUMENTS = 10;

// Zeus caps every free-text agent field at this before intake can answer
// with a typed violation; anything longer is certain to fail untyped.
export const MAX_AGENT_FIELD_LENGTH = 200;

const CONTINGENT_STATUSES = ['listed', 'unlisted', 'in_contract'] as const;
const LENDER_CHOICES = ['non_partner', 'opendoor_home_loans', 'trusted_lender'] as const;

/** The wizard's financing choices that mean a loan — intake requires the down
 * payment and lender for these and rejects both on `cash` / `other`. */
export const isLoanFinancing = (financingType: string | null): boolean =>
  financingType === 'conventional' ||
  financingType === 'fha' ||
  financingType === 'va' ||
  financingType === 'usda';

const submitOfferRequestSchema = z
  .object({
    listingId: z.number().int().positive(),
    priceCents: z.number().int().positive().max(MAX_CENTS),
    sellerConcessionCents: z.number().int().min(0).max(MAX_CENTS),
    buyerAgentCompensationBps: z.number().int().min(0).max(400),
    earnestDepositCents: z.number().int().min(0).max(MAX_CENTS),
    inspectionPeriodDays: z.number().int().min(0).max(20),
    appraisalPeriodDays: z.number().int().min(0).max(20),
    financingPeriodDays: z.number().int().min(0).max(30),
    downPaymentCents: z.number().int().min(0).max(MAX_CENTS).nullable(),
    lenderType: z.enum(LENDER_CHOICES).nullable(),
    financingType: z.enum(FINANCING_CHOICES),
    closeDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    contingentOnSale: z.boolean(),
    contingentHomeStatus: z.enum(CONTINGENT_STATUSES).nullable(),
    buyerFullName: z.string().min(1),
    buyerEmail: z.string().min(1).nullable(),
    agentFirstName: z.string().min(1).max(MAX_AGENT_FIELD_LENGTH),
    agentLastName: z.string().min(1).max(MAX_AGENT_FIELD_LENGTH),
    agentEmail: z.string().min(1),
    agentPhone: z.string().refine((phone) => e164UsPhone(phone) != null),
    agentType: z.enum(AGENT_TYPES),
    agentMlsId: z.string().min(1).max(MAX_AGENT_FIELD_LENGTH).nullable(),
    agentMlsOfficeId: z.string().min(1).max(MAX_AGENT_FIELD_LENGTH).nullable(),
    agentLicenseNumber: z.string().min(1).max(MAX_AGENT_FIELD_LENGTH).nullable(),
    documents: z.array(z.object({ documentId: z.string().min(1) })).max(MAX_OFFER_DOCUMENTS),
  })
  // A contingent offer without its home status must be refused, not quietly
  // submitted as non-contingent.
  .refine((request) => !request.contingentOnSale || request.contingentHomeStatus != null)
  // Blank is allowed. A value must be an address Zeus will accept.
  .refine((request) => request.buyerEmail == null || isValidEmailAddress(request.buyerEmail))
  // Intake requires the loan fields for loan financing and rejects them
  // otherwise; refusing here beats a round-trip that is certain to fail.
  .refine((request) =>
    isLoanFinancing(request.financingType)
      ? request.downPaymentCents != null && request.lenderType != null
      : request.downPaymentCents == null && request.lenderType == null,
  )
  // Intake rejects money above the offer price; the steps enforce the same.
  .refine(
    (request) =>
      request.sellerConcessionCents <= request.priceCents &&
      request.earnestDepositCents <= request.priceCents &&
      (request.downPaymentCents ?? 0) <= request.priceCents,
  )
  // An MLS agent is credited by their MLS ID, and intake refuses an identifier
  // that describes the other kind of agent.
  .refine((request) =>
    request.agentType === 'mls'
      ? request.agentMlsId != null && request.agentLicenseNumber == null
      : request.agentMlsId == null && request.agentMlsOfficeId == null,
  )
  // contactName carries the joined pair and shares the per-field cap, so two
  // individually valid names can still overflow it.
  .refine(
    (request) =>
      `${request.agentFirstName} ${request.agentLastName}`.length <= MAX_AGENT_FIELD_LENGTH,
  );

export type SubmitOfferRequest = z.infer<typeof submitOfferRequestSchema>;

/**
 * The completed wizard draft as a submission request, or undefined while any
 * answer is missing or malformed — every step validates on its own Continue,
 * so a gap here means the draft was tampered with or predates a schema
 * change, and refusing beats submitting altered terms.
 */
export const buildSubmitRequest = (
  listing: ListingDetails,
  draft: OfferDraft | undefined,
): SubmitOfferRequest | undefined => {
  if (draft == null) {
    return undefined;
  }
  const candidate = {
    listingId: listing.id,
    priceCents: draft.priceCents,
    sellerConcessionCents: draft.sellerConcessionCents,
    buyerAgentCompensationBps: draft.buyerAgentCompensationBps,
    earnestDepositCents: draft.earnestDepositCents,
    inspectionPeriodDays: draft.inspectionPeriodDays,
    appraisalPeriodDays: draft.appraisalPeriodDays,
    financingPeriodDays: draft.financingPeriodDays,
    downPaymentCents: draft.downPaymentCents,
    lenderType: draft.lenderType,
    financingType: draft.financingType,
    closeDate: draft.closeDate,
    contingentOnSale: draft.contingentOnSale,
    contingentHomeStatus: draft.contingentHomeStatus,
    buyerFullName: draft.buyerFullName,
    buyerEmail: draft.buyerEmail?.trim() || null,
    agentFirstName: draft.agentFirstName,
    agentLastName: draft.agentLastName,
    agentEmail: draft.agentEmail,
    agentPhone: draft.agentPhone,
    agentType: draft.agentType,
    // Only the identifiers this agent was actually asked for. A value the
    // draft still holds from before the agent type was changed describes an
    // agent who is not submitting, and intake refuses the contradiction.
    agentMlsId: draft.agentType === 'mls' ? draft.agentMlsId : null,
    agentMlsOfficeId: showsMlsOfficeId(listing, draft.agentType) ? draft.agentMlsOfficeId : null,
    agentLicenseNumber: showsAgentLicenseNumber(draft.agentType)
      ? draft.agentLicenseNumber?.trim() || null
      : null,
    documents: documentsForFinancing(draft.documents, draft.financingType).map((document) => ({
      documentId: document.documentId,
    })),
  };
  const parsed = submitOfferRequestSchema.safeParse(candidate);
  return parsed.success ? parsed.data : undefined;
};

const AGENT_TYPE_INPUT: Record<
  (typeof AGENT_TYPES)[number],
  NonNullable<SubmitListingOfferInput['agentType']>
> = {
  mls: 'MLS_AGENT',
  non_mls: 'NON_MLS_AGENT',
};

const FINANCING_TYPE_INPUT: Record<(typeof FINANCING_CHOICES)[number], ListingOfferFinancingType> =
  {
    cash: 'CASH',
    conventional: 'CONVENTIONAL',
    fha: 'FHA_LOAN',
    va: 'VA_LOAN',
    usda: 'USDA_LOAN',
    other: 'DONT_KNOW',
  };

const LENDER_TYPE_INPUT: Record<(typeof LENDER_CHOICES)[number], ListingOfferLenderType> = {
  non_partner: 'NON_PARTNER',
  opendoor_home_loans: 'OPENDOOR_HOME_LOANS',
  trusted_lender: 'TRUSTED_LENDER',
};

const CONTINGENT_STATUS_INPUT: Record<
  (typeof CONTINGENT_STATUSES)[number],
  NonNullable<SubmitListingOfferInput['buyerHomeSaleStatus']>
> = {
  listed: 'LISTED',
  unlisted: 'UNLISTED',
  in_contract: 'IN_CONTRACT',
};

/** The request as the mutation's input — the one place units convert. */
export const submitListingOfferInput = (request: SubmitOfferRequest): SubmitListingOfferInput => ({
  actor: 'BUYER_AGENT',
  listingId: String(request.listingId),
  priceCents: request.priceCents,
  concessionsFromSellerCents: request.sellerConcessionCents,
  brokerCommissionRate: request.buyerAgentCompensationBps / 10000,
  earnestDepositCents: request.earnestDepositCents,
  inspectionPeriodDays: request.inspectionPeriodDays,
  appraisalPeriodDays: request.appraisalPeriodDays,
  financingPeriodDays: request.financingPeriodDays,
  financingType: FINANCING_TYPE_INPUT[request.financingType],
  ...(request.downPaymentCents != null ? { downPaymentCents: request.downPaymentCents } : {}),
  ...(request.lenderType != null ? { lenderType: LENDER_TYPE_INPUT[request.lenderType] } : {}),
  closeOfEscrow: request.closeDate,
  ...(request.contingentOnSale && request.contingentHomeStatus != null
    ? { buyerHomeSaleStatus: CONTINGENT_STATUS_INPUT[request.contingentHomeStatus] }
    : {}),
  buyerName: request.buyerFullName,
  ...(request.buyerEmail != null ? { buyerEmail: request.buyerEmail } : {}),
  agentFirstName: request.agentFirstName,
  agentLastName: request.agentLastName,
  agentType: AGENT_TYPE_INPUT[request.agentType],
  ...(request.agentMlsId != null ? { agentMlsId: request.agentMlsId } : {}),
  ...(request.agentMlsOfficeId != null ? { agentMlsOfficeId: request.agentMlsOfficeId } : {}),
  ...(request.agentLicenseNumber != null ? { agentLicenseNumber: request.agentLicenseNumber } : {}),
  // The whole name as well as the split pair: contact_name names the contact
  // on the offer and resolves the customer record.
  contactName: `${request.agentFirstName} ${request.agentLastName}`,
  contactEmail: request.agentEmail,
  // buildSubmitRequest already refused any number this cannot normalize.
  contactPhone: e164UsPhone(request.agentPhone) ?? request.agentPhone,
  ...(request.documents.length > 0 ? { documents: request.documents } : {}),
});

export interface IntakeViolation {
  /** The mutation input field, or null when the violation spans the contact fields. */
  field: string | null;
  reason: string;
  message: string;
}

/**
 * What submission reports back, as a closed union: the offer was created,
 * strict intake rejected it with typed per-field violations, the caller
 * tripped a rate limit, or the attempt failed outright and can be retried.
 */
export type SubmitOfferResponse =
  | { kind: 'created'; offerId: number }
  | { kind: 'intakeRejected'; message: string; violations: IntakeViolation[] }
  | { kind: 'rateLimited'; retryAfterSeconds: number | null }
  | { kind: 'failed' };

export const errorCode = (error: ClientError): string | undefined => {
  const code = error.response.errors?.[0]?.extensions?.['code'];
  return typeof code === 'string' ? code : undefined;
};

// Zeus's per-field limit and the Rails per-IP bucket both answer RATE_LIMITED
// but with very different windows; the hint tells the wizard which copy fits.
const retryAfterSeconds = (error: ClientError): number | null => {
  const value = error.response.errors?.[0]?.extensions?.['retryAfter'];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const submitOffer = async (request: SubmitOfferRequest): Promise<SubmitOfferResponse> => {
  let data: SubmitAgentListingOfferMutation;
  try {
    // Bounded like the proxy it replaced: a stalled request must fall back to
    // the retriable failure state, not pin the wizard on Submitting forever.
    data = await requestZeusFromBrowser(
      SubmitAgentListingOfferDocument,
      { input: submitListingOfferInput(request) },
      AbortSignal.timeout(15_000),
    );
  } catch (error) {
    if (error instanceof ClientError && errorCode(error) === 'RATE_LIMITED') {
      return { kind: 'rateLimited', retryAfterSeconds: retryAfterSeconds(error) };
    }
    return { kind: 'failed' };
  }
  const result = data.submitListingOffer;
  switch (result.__typename) {
    case 'ListingOfferSubmitted': {
      const offerId = Number(result.offerId);
      return Number.isSafeInteger(offerId) ? { kind: 'created', offerId } : { kind: 'failed' };
    }
    case 'ListingOfferIntakeRejection': {
      return {
        kind: 'intakeRejected',
        message: result.message,
        violations: result.violations.map((violation) => ({
          field: violation.field ?? null,
          reason: violation.reason,
          message: violation.message,
        })),
      };
    }
    // An unknown future union member arrives as bare __typename; the offer
    // state is unknowable, so it reads as a retriable failure.
    default: {
      return { kind: 'failed' };
    }
  }
};
